import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import {
  FaBuilding,
  FaEnvelope,
  FaEye,
  FaEyeSlash,
  FaGlobe,
  FaLock,
  FaMapMarkerAlt,
  FaPhone,
  FaUser,
  FaUsers,
  FaWhatsapp,
} from "react-icons/fa";
import { sendRegisterOtp, verifyRegisterOtp } from "../api/register.api";
import { ASSETS } from "../config/assets";

const COMPANY_SIZES = [
  { value: "", label: "Select company size" },
  { value: "1-10", label: "1–10 Employees" },
  { value: "10-20", label: "10–20 Employees" },
  { value: "20-50", label: "20–50 Employees" },
  { value: "50-100", label: "50–100 Employees" },
  { value: "100+", label: "100+ Employees" },
];

const COUNTRIES = [
  { code: "+91", name: "India", iso: "IN" },
  { code: "+1", name: "United States", iso: "US" },
  { code: "+44", name: "United Kingdom", iso: "GB" },
  { code: "+971", name: "United Arab Emirates", iso: "AE" },
  { code: "+65", name: "Singapore", iso: "SG" },
  { code: "+61", name: "Australia", iso: "AU" },
];

const TAGLINE ="";

const EMPTY_FORM = {
  fullName: "",
  email: "",
  password: "",
  companyName: "",
  companyAddress: "",
  companySize: "",
  companycontact: "",
  companywebsite: "",
};

const buildOtpPayload = (form, country) => ({
  fullName: form.fullName.trim(),
  email: form.email.trim(),
  password: form.password,
  companyName: form.companyName.trim(),
  companyAddress: form.companyAddress.trim(),
  companySize: form.companySize,
  companycontact: form.companycontact.trim(),
  companywebsite: form.companywebsite.trim(),
  selectedCountry: country,
});

const COUNTRY_OPTIONS = [
  { value: "", label: "Select country" },
  ...COUNTRIES.map((c) => ({ value: c.iso, label: c.name })),
];

function RegisterSelect({
  id,
  value,
  onChange,
  options,
  placeholder,
  required,
  ariaLabel,
}) {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const triggerRef = useRef(null);

  const selectableOptions = options.filter((opt) => opt.value !== "");
  const selected = options.find((opt) => opt.value === value);

  const updateMenuPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const gap = 4;
    const spaceBelow = window.innerHeight - rect.bottom - gap - 8;
    const spaceAbove = rect.top - gap - 8;
    const preferredHeight = Math.min(240, selectableOptions.length * 44);
    const openUpward = spaceBelow < 140 && spaceAbove > spaceBelow;
    const maxHeight = Math.min(preferredHeight, openUpward ? spaceAbove : spaceBelow);

    setMenuStyle({
      left: rect.left,
      width: rect.width,
      maxHeight: Math.max(maxHeight, 120),
      top: openUpward ? undefined : rect.bottom + gap,
      bottom: openUpward ? window.innerHeight - rect.top + gap : undefined,
    });
  }, [selectableOptions.length]);

  useEffect(() => {
    if (!open) return undefined;

    updateMenuPosition();
    const onLayoutChange = () => updateMenuPosition();
    window.addEventListener("resize", onLayoutChange);
    window.addEventListener("scroll", onLayoutChange, true);

    return () => {
      window.removeEventListener("resize", onLayoutChange);
      window.removeEventListener("scroll", onLayoutChange, true);
    };
  }, [open, updateMenuPosition]);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      const target = event.target;
      if (triggerRef.current?.contains(target)) return;
      if (target instanceof Element && target.closest(".register-select-menu-portal")) {
        return;
      }
      setOpen(false);
    };

    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const toggleOpen = () => {
    setOpen((prev) => {
      const next = !prev;
      if (next) {
        requestAnimationFrame(updateMenuPosition);
      }
      return next;
    });
  };

  const menu =
    open &&
    menuStyle &&
    createPortal(
      <ul
        className="register-select-menu register-select-menu-portal"
        role="listbox"
        aria-labelledby={id}
        style={{
          position: "fixed",
          left: menuStyle.left,
          width: menuStyle.width,
          maxHeight: menuStyle.maxHeight,
          top: menuStyle.top,
          bottom: menuStyle.bottom,
          zIndex: 10000,
        }}
      >
        {selectableOptions.map((opt) => (
          <li key={opt.value} role="none">
            <button
              type="button"
              role="option"
              aria-selected={value === opt.value}
              className={`register-select-option${value === opt.value ? " is-selected" : ""}`}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
            >
              {opt.label}
            </button>
          </li>
        ))}
      </ul>,
      document.body,
    );

  return (
    <>
      <div className="register-select-field">
        <button
          ref={triggerRef}
          id={id}
          type="button"
          className={`register-select-trigger${value ? "" : " is-placeholder"}`}
          aria-label={ariaLabel}
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={toggleOpen}
        >
          <span className="register-select-trigger-text">
            {selected?.label || placeholder}
          </span>
        </button>
        {required && (
          <input
            className="register-select-hidden-required"
            tabIndex={-1}
            value={value}
            required
            readOnly
            aria-hidden="true"
            onChange={() => {}}
          />
        )}
      </div>
      {menu}
    </>
  );
}

const Register = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY_FORM);
  const [country, setCountry] = useState(null);
  const [formStep, setFormStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState("form");
  const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""]);
  const [otpExpiresAt, setOtpExpiresAt] = useState(null);
  const [otpTimer, setOtpTimer] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const otpRefs = useRef([]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [formStep]);

  useEffect(() => {
    if (!otpExpiresAt || step !== "otp") return undefined;

    const tick = () => {
      const end = new Date(otpExpiresAt).getTime();
      const diff = end - Date.now();
      if (diff <= 0) {
        setOtpTimer("expired");
        return;
      }
      const mins = Math.floor(diff / 60000);
      const secs = Math.floor((diff % 60000) / 1000);
      setOtpTimer(`${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`);
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [otpExpiresAt, step]);

  const onChange = (e) => {
    const { name, value } = e.target;
    const nextValue =
      name === "companycontact" ? value.replace(/\D/g, "").slice(0, 10) : value;
    setForm((prev) => ({ ...prev, [name]: nextValue }));
    setError("");
  };

  const validateAccount = () => {
    if (!form.fullName.trim() || !form.email.trim() || !form.password) {
      setError("Please fill all required fields.");
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setError("Please enter a valid email address.");
      return false;
    }
    if (form.password.length < 6) {
      setError("Password must be at least 6 characters.");
      return false;
    }
    return true;
  };

  const validateCompany = () => {
    if (!country?.code) {
      setError("Please select your country.");
      return false;
    }
    if (
      !form.companyName.trim() ||
      !form.companyAddress.trim() ||
      !form.companySize ||
      !form.companycontact.trim()
    ) {
      setError("Please fill all required fields.");
      return false;
    }
    if (!/^\d{10}$/.test(form.companycontact.trim())) {
      setError("WhatsApp number must be 10 digits.");
      return false;
    }
    return true;
  };

  const requestOtp = useCallback(async () => {
    const accountReady =
      form.fullName.trim() &&
      form.email.trim() &&
      form.password &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()) &&
      form.password.length >= 6;
    const companyReady =
      country?.code &&
      form.companyName.trim() &&
      form.companyAddress.trim() &&
      form.companySize &&
      /^\d{10}$/.test(form.companycontact.trim());

    if (!accountReady || !companyReady) {
      if (!country?.code) setError("Please select your country.");
      else if (!/^\d{10}$/.test(form.companycontact.trim())) {
        setError("WhatsApp number must be 10 digits.");
      } else {
        setError("Please fill all required fields.");
      }
      return false;
    }

    setLoading(true);
    setError("");
    try {
      const res = await sendRegisterOtp(buildOtpPayload(form, country));
      setOtpExpiresAt(res?.expiresAt || res?.data?.expiresAt || null);
      setOtpDigits(["", "", "", "", "", ""]);
      setStep("otp");
      return true;
    } catch (err) {
      setError(err.message || "Failed to send OTP");
      return false;
    } finally {
      setLoading(false);
    }
  }, [form, country]);

  const onSubmitForm = async (e) => {
    e.preventDefault();
    if (formStep === 1) {
      if (!validateAccount()) return;
      setError("");
      setFormStep(2);
      return;
    }
    if (!validateCompany()) return;
    await requestOtp();
  };

  const onOtpChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;
    const next = [...otpDigits];
    next[index] = value;
    setOtpDigits(next);
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const onOtpPaste = (e) => {
    const text = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!text) return;
    e.preventDefault();
    const next = ["", "", "", "", "", ""];
    text.split("").forEach((ch, i) => {
      next[i] = ch;
    });
    setOtpDigits(next);
    otpRefs.current[Math.min(text.length, 5)]?.focus();
  };

  const onOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const onVerifyOtp = async (e) => {
    e.preventDefault();
    const finalOtp = otpDigits.join("");
    if (finalOtp.length !== 6) {
      setError("Please enter the 6-digit OTP.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const res = await verifyRegisterOtp({
        ...buildOtpPayload(form, country),
        finalOtp,
      });
      if (res?.success === false) {
        throw new Error(res.message || "OTP verification failed");
      }
      setSuccessMessage(res?.message || "Your account has been created successfully.");
      setStep("success");
    } catch (err) {
      setError(err.message || "OTP verification failed");
    } finally {
      setLoading(false);
    }
  };

  const onResendOtp = async (e) => {
    e.preventDefault();
    await requestOtp();
  };

  return (
    <div className="register-shell">
      <aside className="login-hero-panel">
        <img src={ASSETS.registerHero} alt="" className="login-hero-photo" />
        <div className="login-hero-content">
          <p className="login-hero-tagline">{TAGLINE}</p>
        </div>
      </aside>

      <main className="register-form-panel">
        <div className="register-card">
          <div className="login-card-brand">
            <img
              src={ASSETS.brandLogo}
              alt={ASSETS.brandName}
              className="login-card-logo"
            />
            <p className="login-product">
              <span className="login-wa-dot" aria-hidden>
                <FaWhatsapp />
              </span>
              WhatsApp Business API
            </p>
          </div>

          {step !== "success" && (
            <ol className="register-steps" aria-label="Registration progress">
              <li className={`register-step${formStep === 1 ? " is-active" : " is-done"}`}>
                <span className="register-step-index" aria-hidden="true">
                  1
                </span>
                Account
              </li>
              <li className={`register-step-rule${formStep > 1 ? " is-done" : ""}`} aria-hidden="true" />
              <li className={`register-step${formStep === 2 ? " is-active" : ""}`}>
                <span className="register-step-index" aria-hidden="true">
                  2
                </span>
                Company
              </li>
            </ol>
          )}

          <header className="login-card-intro">
            <h1 className="login-title">
              {step === "success"
                ? "You're all set"
                : formStep === 1
                  ? "Create account"
                  : "Company details"}
            </h1>
            <p className="login-subtitle">
              {step === "success"
                ? "Your workspace is ready. Sign in to continue."
                : formStep === 1
                  ? "Enter your details to get started."
                  : "Tell us about your business."}
            </p>
          </header>

          {error && step !== "success" && step !== "otp" && (
            <p className="login-error" role="alert">
              {error}
            </p>
          )}

          {step === "form" && (
            <form onSubmit={onSubmitForm} className="register-form">
              {formStep === 1 && (
                <>
                  <label className="login-label" htmlFor="register-full-name">
                    Full name
                  </label>
                  <div className="login-field">
                    <FaUser className="login-field-icon" aria-hidden="true" />
                    <input
                      id="register-full-name"
                      type="text"
                      name="fullName"
                      value={form.fullName}
                      onChange={onChange}
                      placeholder="Jane Smith"
                      className="login-input"
                      required
                      autoComplete="name"
                    />
                  </div>

                  <label className="login-label" htmlFor="register-email">
                    Email
                  </label>
                  <div className="login-field">
                    <FaEnvelope className="login-field-icon" aria-hidden="true" />
                    <input
                      id="register-email"
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={onChange}
                      placeholder="name@company.com"
                      className="login-input"
                      required
                      autoComplete="email"
                    />
                  </div>

                  <label className="login-label" htmlFor="register-password">
                    Password
                  </label>
                  <div className="login-field">
                    <FaLock className="login-field-icon" aria-hidden="true" />
                    <input
                      id="register-password"
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={form.password}
                      onChange={onChange}
                      placeholder="At least 6 characters"
                      className="login-input"
                      required
                      minLength={6}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="login-password-toggle"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </>
              )}

              {formStep === 2 && (
                <div className="register-field-grid">
                  <div className="register-field">
                    <label className="login-label" htmlFor="register-company-name">
                      Company name
                    </label>
                    <div className="login-field">
                      <FaBuilding className="login-field-icon" aria-hidden="true" />
                      <input
                        id="register-company-name"
                        type="text"
                        name="companyName"
                        value={form.companyName}
                        onChange={onChange}
                        placeholder="Acme Pvt Ltd"
                        className="login-input"
                        required
                        autoComplete="organization"
                      />
                    </div>
                  </div>

                  <div className="register-field">
                    <label className="login-label" htmlFor="register-company-address">
                      Company address
                    </label>
                    <div className="login-field">
                      <FaMapMarkerAlt className="login-field-icon" aria-hidden="true" />
                      <input
                        id="register-company-address"
                        type="text"
                        name="companyAddress"
                        value={form.companyAddress}
                        onChange={onChange}
                        placeholder="City, state"
                        className="login-input"
                        required
                        autoComplete="street-address"
                      />
                    </div>
                  </div>

                  <div className="register-field">
                    <label className="login-label" htmlFor="register-country">
                      Country
                    </label>
                    <div className="login-field">
                      <FaGlobe className="login-field-icon" aria-hidden="true" />
                      <RegisterSelect
                        id="register-country"
                        value={country?.iso || ""}
                        onChange={(iso) => {
                          const next = COUNTRIES.find((c) => c.iso === iso);
                          setCountry(next || null);
                          setError("");
                        }}
                        options={COUNTRY_OPTIONS}
                        placeholder="Select country"
                        required
                        ariaLabel="Country"
                      />
                    </div>
                  </div>

                  <div className="register-field">
                    <label className="login-label" htmlFor="register-company-contact">
                      WhatsApp number
                    </label>
                    <div className="login-field">
                      <FaPhone className="login-field-icon" aria-hidden="true" />
                      <span className="register-phone-prefix">
                        {country?.code || "+"}
                      </span>
                      <input
                        id="register-company-contact"
                        type="tel"
                        name="companycontact"
                        value={form.companycontact}
                        onChange={onChange}
                        placeholder="10-digit number"
                        className="login-input"
                        required
                        autoComplete="tel"
                        inputMode="numeric"
                        maxLength={10}
                        pattern="[0-9]{10}"
                        title="Enter a 10-digit contact number"
                      />
                    </div>
                  </div>

                  <div className="register-field">
                    <label className="login-label" htmlFor="register-company-size">
                      Company size
                    </label>
                    <div className="login-field">
                      <FaUsers className="login-field-icon" aria-hidden="true" />
                      <RegisterSelect
                        id="register-company-size"
                        value={form.companySize}
                        onChange={(nextValue) => {
                          setForm((prev) => ({ ...prev, companySize: nextValue }));
                          setError("");
                        }}
                        options={COMPANY_SIZES}
                        placeholder="Select company size"
                        required
                        ariaLabel="Company size"
                      />
                    </div>
                  </div>

                  <div className="register-field">
                    <label className="login-label" htmlFor="register-company-website">
                      Website <span className="register-optional">(optional)</span>
                    </label>
                    <div className="login-field">
                      <FaGlobe className="login-field-icon" aria-hidden="true" />
                      <input
                        id="register-company-website"
                        type="url"
                        name="companywebsite"
                        value={form.companywebsite}
                        onChange={onChange}
                        placeholder="https://company.com"
                        className="login-input"
                        autoComplete="url"
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="register-actions">
                {formStep === 2 && (
                  <button
                    type="button"
                    className="register-back-btn"
                    onClick={() => {
                      setFormStep(1);
                      setError("");
                    }}
                  >
                    Back
                  </button>
                )}
                <button type="submit" className="login-submit" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="spinner" />
                      Creating...
                    </>
                  ) : formStep === 1 ? (
                    "Continue"
                  ) : (
                    "Create account"
                  )}
                </button>
              </div>
            </form>
          )}

          {step === "success" && (
            <div className="register-success-panel">
              <img src={ASSETS.checkmark} alt="" className="register-success-icon" />
              <p className="register-success-text">{successMessage}</p>
              <button
                type="button"
                className="login-submit"
                onClick={() => navigate("/login")}
              >
                Go to Login
              </button>
            </div>
          )}

          {step !== "success" && (
            <>
              <p className="login-footer">
                Already registered?{" "}
                <Link to="/login" className="login-link">
                  Sign in
                </Link>
              </p>
              <nav className="login-footer-links">
                <Link to="/" className="login-footer-meta">
                  Home
                </Link>
                <span aria-hidden="true">·</span>
                <Link to="/service-detail" className="login-footer-meta">
                  Service details
                </Link>
              </nav>
            </>
          )}
        </div>
      </main>

      {step === "otp" && (
        <div
          className="register-otp-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="register-otp-title"
        >
          <div className="register-otp-card">
            <span className="register-otp-icon" aria-hidden="true">
              <FaWhatsapp />
            </span>
            <h3 id="register-otp-title">Verify your WhatsApp</h3>
            <p className="register-otp-hint">
              Enter the 6-digit code sent to{" "}
              <strong>
                {country?.code}
                {form.companycontact}
              </strong>
            </p>

            {error && (
              <p className="login-error" role="alert">
                {error}
              </p>
            )}

            <div className="register-otp-inputs">
              {otpDigits.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => {
                    otpRefs.current[index] = el;
                  }}
                  id={`otp-${index}`}
                  type="text"
                  inputMode="numeric"
                  autoComplete={index === 0 ? "one-time-code" : "off"}
                  maxLength={1}
                  value={digit}
                  onChange={(e) => onOtpChange(index, e.target.value)}
                  onKeyDown={(e) => onOtpKeyDown(index, e)}
                  onPaste={index === 0 ? onOtpPaste : undefined}
                  className="register-otp-box"
                  aria-label={`OTP digit ${index + 1}`}
                />
              ))}
            </div>

            <button
              type="button"
              className="login-submit register-otp-verify"
              onClick={onVerifyOtp}
              disabled={loading}
            >
              {loading ? "Verifying…" : "Verify OTP"}
            </button>

            <div className="register-otp-footer">
              <span>Didn&apos;t receive the code?</span>
              {otpTimer === "expired" ? (
                <button type="button" className="register-otp-resend" onClick={onResendOtp}>
                  Resend OTP
                </button>
              ) : (
                <p>OTP expires in {otpTimer || "—"}</p>
              )}
            </div>

            <button
              type="button"
              className="register-otp-back"
              onClick={() => {
                setStep("form");
                setError("");
              }}
            >
              Back to form
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Register;
